/**
 * The jump bar for a long form dialog.
 *
 * Complements the sticky headings: those tell you where you *are*, this tells you what else
 * exists and gets you there in one click instead of a long scroll. The active chip is driven
 * by what is actually on screen, so it doubles as a position indicator while scrolling.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

export interface FormSectionNavItem {
    id: string;
    label: string;
}

/** Nearest ancestor that actually scrolls — the dialog body, not the window. */
const findScrollParent = (node: HTMLElement | null): HTMLElement | null => {
    let current = node?.parentElement ?? null;
    while (current) {
        const { overflowY } = window.getComputedStyle(current);
        if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
            return current;
        }
        current = current.parentElement;
    }
    return null;
};

/**
 * Publish an element's height as a CSS custom property on a target element, kept in sync as it
 * resizes. Used so sticky section headings can sit exactly under the dialog header without
 * anyone hardcoding its height — it changes with the title, the badges and the viewport.
 */
export const useMeasuredHeight = (
    source: React.RefObject<HTMLElement>,
    target: React.RefObject<HTMLElement>,
    cssVar: string,
) => {
    useLayoutEffect(() => {
        const sourceEl = source.current;
        const targetEl = target.current;
        if (!sourceEl || !targetEl) return;

        const apply = () => targetEl.style.setProperty(cssVar, `${sourceEl.offsetHeight}px`);
        apply();

        const observer = new ResizeObserver(apply);
        observer.observe(sourceEl);
        return () => observer.disconnect();
    }, [source, target, cssVar]);
};

export function FormSectionNav({ items, className }: { items: FormSectionNavItem[]; className?: string }) {
    const navRef = useRef<HTMLDivElement>(null);
    const [activeId, setActiveId] = useState<string | null>(items[0]?.id ?? null);

    useEffect(() => {
        const root = findScrollParent(navRef.current);
        const nav = navRef.current;
        if (!root || !nav) return;

        const elements = items
            .map((item) => document.getElementById(item.id))
            .filter((el): el is HTMLElement => Boolean(el));
        if (elements.length === 0) return;

        let frame = 0;
        const recompute = () => {
            frame = 0;
            const rootTop = root.getBoundingClientRect().top;
            /*
             * The "fold" is the bottom of the sticky header — everything above it is hidden
             * behind the header, so it is the line that decides which section is being read.
             * Deriving it from the nav's own position keeps it correct as the header grows or
             * shrinks, which a hardcoded offset would not.
             */
            const fold = nav.getBoundingClientRect().bottom - rootTop;

            /*
             * The last section that has started by the fold line — which is exactly the one
             * whose heading is pinned, so the chip and the heading can never disagree.
             *
             * Deliberately not "the section straddling the fold": sections are separated by a
             * gap, and in that gap no section straddles anything, which would make the chip
             * flick back to the first item every time you scrolled across a boundary.
             */
            let current = elements[0].id;
            for (const el of elements) {
                const top = el.getBoundingClientRect().top - rootTop;
                if (top <= fold + 1) {
                    current = el.id;
                } else {
                    break;
                }
            }
            setActiveId(current);
        };

        /*
         * A scroll listener rather than IntersectionObserver alone: these sections are tall
         * enough that a long scroll can happen entirely inside one, crossing no threshold, and
         * an observer-only implementation then leaves the chip pointing at a stale section.
         */
        const onScroll = () => {
            if (frame) return;
            frame = requestAnimationFrame(recompute);
        };

        recompute();
        root.addEventListener('scroll', onScroll, { passive: true });
        // Sections grow and shrink as fields expand, which moves the fold without any scroll.
        const resize = new ResizeObserver(onScroll);
        elements.forEach((el) => resize.observe(el));

        return () => {
            root.removeEventListener('scroll', onScroll);
            resize.disconnect();
            if (frame) cancelAnimationFrame(frame);
        };
    }, [items]);

    const jumpTo = useCallback((id: string) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveId(id);
    }, []);

    if (items.length < 2) return null;

    return (
        <div
            ref={navRef}
            className={cn('-mx-1 flex gap-1 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden', className)}
        >
            {items.map((item) => (
                <button
                    key={item.id}
                    type="button"
                    onClick={() => jumpTo(item.id)}
                    aria-current={activeId === item.id ? 'true' : undefined}
                    className={cn(
                        'shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                        activeId === item.id
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )}
                >
                    {item.label}
                </button>
            ))}
        </div>
    );
}
